import {
  CieLoginButton,
  CnsLoginButton,
  ProfessionalSpidLoginButton,
  SpidLoginButton,
} from '@framework/auth-open-web';
import { t } from '@framework/i18n';
import { IFrontLoginFormProps } from '@framework/theme/src/front/FrontLoginForm';
import {
  LoaderSpin,
  LoginCard,
  RowElement,
  useHistory,
  useSessionStorageLocation,
  VerticalSpace,
} from '@framework/ui';
import { Row } from 'antd';
import { down } from 'styled-breakpoints';
import { useBreakpoint } from 'styled-breakpoints/react-styled';
import styled from 'styled-components';
import { EmployeeLoginForm } from './login/EmployeeLoginForm';
import { LoginBlock } from './login/LoginBlock';
import { LoginBlockStepsForeignUsers } from './login/LoginBlockStepsForeignUsers';
import { LoginBlockStepsPaUsers } from './login/LoginBlockStepsPaUsers';
import { LoginCol } from './login/LoginCol';

const PageLoginExtraLarge = styled.div`
  max-width: 1380px;
  margin: 0 auto;
`;

/**
 * Form di login all'applicativo frontoffice.
 */
export function FrontLoginForm(props: IFrontLoginFormProps) {
  const { loading = false, resolvePublicUrl, onLogin, externalLogin } = props;

  const history = useHistory();
  const searchParams = new URLSearchParams(history.location.search);
  const isOnlyEmployee = searchParams.get('dipendente') === 'true';

  const needMobileWidht = useBreakpoint(down('md'));

  // Per il redirect post-login
  useSessionStorageLocation();

  if (isOnlyEmployee) {
    return (
      <div>
        <LoaderSpin spinning={loading} transparent>
          <LoginCard
            srOnlyText={t(
              'user.front-login.page-title.h1.login-page',
              'Pagina di Login.',
            )}
            title={null}
            fullWidth
          >
            <Row gutter={24}>
              <EmployeeLoginForm
                onLogin={onLogin}
                resolvePublicUrl={resolvePublicUrl}
                skipLoginBlock
              />
            </Row>
          </LoginCard>
        </LoaderSpin>
      </div>
    );
  }

  return (
    <div>
      <LoaderSpin spinning={loading} transparent hasFullWidthChildren noPadding>
        <LoginCard
          srOnlyText={t(
            'user.front-login.page-title.h1.login-page',
            'Pagina di Login.',
          )}
          title={null}
          fullWidth
        >
          <Row gutter={24}>
            <LoginCol
              title={t(
                'user.front-login.title-utenti-italiani',
                'Utenti italiani',
              )}
            >
              <RowElement gutter={0} forceMobile={needMobileWidht}>
                {/* SPID */}
                <LoginBlock
                  left
                  before={
                    <div>
                      {t(
                        'user.front-login.text.spid',
                        'SPID è il sistema di accesso che consente di utilizzare, con un’identità digitale unica, i servizi online della Pubblica Amministrazione e dei privati accreditati.',
                      )}
                    </div>
                  }
                  link={
                    <a
                      href={t(
                        'user.front-login.spid-link.info-url',
                        'https://www.spid.gov.it',
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t(
                        'user.front-login.spid-link.info',
                        'Maggiori informazioni su SPID',
                      )}
                    </a>
                  }
                  button={
                    <SpidLoginButton
                      fullWidth
                      resolvePublicUrl={resolvePublicUrl}
                    />
                  }
                />
                {/* CIE */}
                <LoginBlock
                  left
                  right
                  before={
                    <div>
                      {t(
                        'user.front-login.text.cie',
                        'CIE è la Carta di Identità Elettronica che, oltre ad attestare l’identità del cittadino, permette l’accesso ai servizi digitali della Pubblica Amministrazione.',
                      )}
                    </div>
                  }
                  link={
                    <a
                      href={t(
                        'user.front-login.cie-link.info-url',
                        'https://www.cartaidentita.interno.gov.it',
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t(
                        'user.front-login.cie-link.info',
                        'Maggiori informazioni su CIE',
                      )}
                    </a>
                  }
                  button={
                    <CieLoginButton
                      resolvePublicUrl={resolvePublicUrl}
                      fullWidth
                    />
                  }
                />
                {/* CNS  */}
                <LoginBlock
                  right
                  before={
                    <div>
                      {t(
                        'user.front-login.text.cns',
                        'La CNS è un dispositivo dotato di certificato digitale per accedere ai servizi online della PA. Per l’utilizzo è necessario installare i driver forniti dall’emittente.',
                      )}
                    </div>
                  }
                  link={
                    <a
                      href={t(
                        'user.front-login.cns-link.info-url',
                        'https://www.agid.gov.it/it/piattaforme/carta-nazionale-servizi',
                      )}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {t(
                        'user.front-login.cns-link.info',
                        'Maggiori informazioni su CNS',
                      )}
                    </a>
                  }
                  button={<CnsLoginButton fullWidth />}
                />
              </RowElement>
            </LoginCol>
          </Row>
          <VerticalSpace size={24} />
          <Row gutter={[24, 24]} wrap={!!needMobileWidht}>
            {/* EIDAS - Extra UE */}
            <LoginCol lang="en-GB" hasBackground title={'Foreign users'}>
              <LoginBlockStepsForeignUsers
                resolvePublicUrl={resolvePublicUrl}
                onLogin={onLogin}
              />
            </LoginCol>
            {/* Dipendenti PA */}
            <LoginCol
              style={{ height: '100%' }}
              hasBackground
              title={t(
                'user.front-login.title-accesso-dipendenti',
                'Accesso dipendenti',
              )}
            >
              <LoginBlockStepsPaUsers
                onLogin={onLogin}
                resolvePublicUrl={resolvePublicUrl}
              />
            </LoginCol>
            {/* SPID Professionale */}
            <LoginCol
              style={{ height: '100%' }}
              hasBackground
              title={t(
                'user.front-login.title-spid-professionale',
                'SPID Professionale',
              )}
            >
              <LoginBlock
                before={
                  <div>
                    {t(
                      'user.front-login.text.spid-professionale',
                      'Lo SPID Professionale consente a imprese e professionisti di accedere ai servizi online della PA qualificandosi come rappresentanti di una persona giuridica.',
                    )}
                  </div>
                }
                link={
                  <a
                    href={t(
                      'user.front-login.spid-professionale-link.info-url',
                      'https://www.spid.gov.it/aziende/',
                    )}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t(
                      'user.front-login.spid-professionale-link.info',
                      'Maggiori informazioni',
                    )}
                  </a>
                }
                button={
                  <ProfessionalSpidLoginButton
                    fullWidth
                    resolvePublicUrl={resolvePublicUrl}
                  />
                }
              />
            </LoginCol>
          </Row>
        </LoginCard>
      </LoaderSpin>
    </div>
  );
}
